import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface SportsInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const SportsInsights: React.FC<SportsInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Active bullet index logic
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Slide-in from right: translateX from 40 to 0 over 12 frames
  const slideX = interpolate(elapsedFrames, [0, 12], [40, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const bulletOpacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Card entrance — triggered once when first bullet starts
  const firstBulletStart = (bullets[0].startMs / 1000) * fps;
  const cardElapsed = Math.max(0, globalFrame - firstBulletStart);

  const cardOpacity = interpolate(cardElapsed, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        flex: 1,
        padding: "10px 0",
        zIndex: 10,
        opacity: cardOpacity,
      }}
    >
      {/* Outer card */}
      <div
        style={{
          width: "100%",
          background: "#0a0a0a",
          borderLeft: "5px solid #ff6b00",
          padding: "32px 36px",
          borderRadius: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          overflow: "hidden",
        }}
      >
        {/* Large italic background number */}
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "16px",
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: "120px",
            fontWeight: 900,
            fontStyle: "italic",
            color: "rgba(255, 107, 0, 0.1)",
            lineHeight: 1,
            userSelect: "none",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          0{activeIndex + 1}
        </div>

        {/* Stat label + brand badge */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
          <div
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: "13px",
              fontWeight: 400,
              color: "#ff6b00",
              letterSpacing: "3px",
              textTransform: "uppercase",
            }}
          >
            PLAY #{activeIndex + 1}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#ff6b00"
                accentColor="#ff6b00"
                size={72}
                style="badge"
                opacity={bulletOpacity}
              />
            ) : null;
          })()}
        </div>

        {/* Progress dots */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          {bullets.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === activeIndex ? "20px" : "8px",
                height: "8px",
                borderRadius: i === activeIndex ? "4px" : "50%",
                backgroundColor:
                  i <= activeIndex ? "#ff6b00" : "rgba(255, 107, 0, 0.25)",
                transition: "all 0.2s ease",
              }}
            />
          ))}
        </div>

        {/* Bullet text — slides in from right */}
        <div
          style={{
            opacity: bulletOpacity,
            transform: `translateX(${slideX}px)`,
            minHeight: "280px",
            display: "flex",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <p
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: "64px",
              fontWeight: 900,
              fontStyle: "italic",
              color: "#ffffff",
              textTransform: "uppercase",
              letterSpacing: "1px",
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            <HighlightText
              highlightStyle={{
                fontWeight: 900,
                fontStyle: "italic",
                color: "#ff6b00",
                display: "inline",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
